import { Helmet } from "react-helmet-async";
import { Navbar } from "@/components/layout/Navbar";
import { AnchorIcon } from "@/components/icons/AnchorIcon";
import { Link } from "react-router-dom";
import { Separator } from "@/components/ui/separator";

const TermsOfService = () => {
  const lastUpdated = "April 3, 2026";

  return (
    <div className="min-h-screen bg-background">
      <Helmet>
        <title>Terms of Service | HostCaptain</title>
        <meta name="description" content="HostCaptain's terms of service. Read the terms governing your use of the HostCaptain platform." />
        <link rel="canonical" href="https://hostcaptain.co/terms" />
      </Helmet>

      <Navbar />

      <main className="container mx-auto px-6 py-16 max-w-3xl">
        <h1 className="text-3xl font-bold mb-2">Terms of Service</h1>
        <p className="text-muted-foreground mb-8">Last updated: {lastUpdated}</p>

        <div className="prose prose-slate dark:prose-invert max-w-none space-y-8">

          {/* Introduction */}
          <section>
            <h2 className="text-xl font-semibold">1. Introduction</h2>
            <p>
              Welcome to HostCaptain, a product of <strong>MC Capital Solutions LLC</strong>, a company registered in the State of California, United States ("Company", "we", "us", or "our"). These Terms of Service ("Terms") govern your access to and use of the HostCaptain platform, including our website, applications, and related services (collectively, the "Service").
            </p>
            <p>
              By creating an account or using the Service, you agree to be bound by these Terms and our{" "}
              <Link to="/privacy" className="text-primary hover:underline">Privacy Policy</Link>. If you do not agree to these Terms, do not use the Service.
            </p>
          </section>

          <Separator />

          {/* Eligibility */}
          <section>
            <h2 className="text-xl font-semibold">2. Eligibility</h2>
            <p>
              You must be at least 18 years of age and capable of entering into a legally binding agreement to use the Service. By using HostCaptain, you represent and warrant that you meet these requirements.
            </p>
          </section>

          <Separator />

          {/* Account Registration */}
          <section>
            <h2 className="text-xl font-semibold">3. Account Registration</h2>
            <ul className="list-disc pl-6 space-y-1">
              <li>You must provide accurate, complete, and current information when creating your account.</li>
              <li>You are responsible for maintaining the confidentiality of your login credentials.</li>
              <li>You are responsible for all activities that occur under your account.</li>
              <li>You must notify us immediately of any unauthorized use of your account.</li>
            </ul>
          </section>

          <Separator />

          {/* Acceptable Use */}
          <section>
            <h2 className="text-xl font-semibold">4. Acceptable Use</h2>
            <p>You agree not to:</p>
            <ul className="list-disc pl-6 space-y-1">
              <li>Use the Service for any unlawful purpose or in violation of any applicable laws.</li>
              <li>Upload false, misleading, or fraudulent data to the platform.</li>
              <li>Attempt to gain unauthorized access to any part of the Service, other accounts, or our systems.</li>
              <li>Interfere with or disrupt the integrity or performance of the Service.</li>
              <li>Reverse engineer, decompile, or disassemble any part of the Service.</li>
              <li>Use the Service to compete directly with HostCaptain or to build a similar product.</li>
              <li>Share your account credentials with third parties or allow others to use your account.</li>
            </ul>
          </section>

          <Separator />

          {/* Subscription & Billing */}
          <section>
            <h2 className="text-xl font-semibold">5. Subscription and Billing</h2>

            <h3 className="text-lg font-medium mt-4">Plans</h3>
            <p>
              HostCaptain offers both free and paid subscription plans. Features available to you depend on your current plan. Plan details and pricing are described on our{" "}
              <Link to="/pricing" className="text-primary hover:underline">Pricing page</Link>.
            </p>

            <h3 className="text-lg font-medium mt-4">Payment</h3>
            <ul className="list-disc pl-6 space-y-1">
              <li>Paid subscriptions are billed monthly in advance through Stripe.</li>
              <li>By subscribing to a paid plan, you authorize us to charge your payment method on a recurring basis.</li>
              <li>All fees are stated in US dollars unless otherwise noted.</li>
            </ul>

            <h3 className="text-lg font-medium mt-4">Cancellation</h3>
            <ul className="list-disc pl-6 space-y-1">
              <li>You may cancel your subscription at any time from your account settings.</li>
              <li>Cancellation takes effect at the end of the current billing period — you will retain access to paid features until then.</li>
              <li>We do not provide prorated refunds for partial billing periods.</li>
            </ul>

            <h3 className="text-lg font-medium mt-4">Price Changes</h3>
            <p>
              We may change subscription prices with at least 30 days' notice. Continued use of the Service after a price change constitutes acceptance of the new pricing.
            </p>
          </section>

          <Separator />

          {/* AI Features Disclaimer */}
          <section>
            <h2 className="text-xl font-semibold">6. AI Features — Captain AI Disclaimer</h2>
            <div className="bg-primary/5 border border-primary/20 rounded-lg p-4">
              <p>
                HostCaptain includes AI-powered features, including "Captain AI," that analyze your business data and provide recommendations, insights, and suggestions.
              </p>
              <p className="mt-2 font-semibold">
                Captain AI's output is for informational purposes only and does not constitute financial, tax, legal, or investment advice.
              </p>
              <ul className="list-disc pl-6 space-y-1 mt-2">
                <li>AI-generated recommendations may be inaccurate, incomplete, or unsuitable for your specific situation.</li>
                <li>You should independently verify any AI-generated insights before making business decisions.</li>
                <li>We strongly recommend consulting qualified financial, tax, and legal professionals for advice specific to your circumstances.</li>
                <li>HostCaptain and MC Capital Solutions LLC are not liable for any decisions made based on AI-generated content.</li>
              </ul>
            </div>
          </section>

          <Separator />

          {/* Bank Account Linking */}
          <section>
            <h2 className="text-xl font-semibold">7. Bank Account Linking</h2>
            <p>
              If you choose to use the bank account linking feature (powered by Stripe Financial Connections), you explicitly consent to HostCaptain accessing read-only transaction data from your connected financial accounts. This access is governed by both these Terms and Stripe's{" "}
              <a href="https://stripe.com/legal/financialconnections" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">
                Financial Connections Terms
              </a>.
            </p>
            <ul className="list-disc pl-6 space-y-1">
              <li>Linking is voluntary and can be revoked at any time.</li>
              <li>HostCaptain uses transaction data solely to help you manage and categorize your business expenses.</li>
              <li>We do not access, store, or share your bank login credentials.</li>
            </ul>
          </section>

          <Separator />

          {/* Data Ownership */}
          <section>
            <h2 className="text-xl font-semibold">8. Your Data</h2>
            <p>
              You retain ownership of all data you upload or create in HostCaptain (vehicle records, trips, expenses, receipts, etc.). By using the Service, you grant us a limited license to use, process, and store your data solely to provide the Service to you.
            </p>
            <ul className="list-disc pl-6 space-y-1">
              <li>You can export your data at any time using available export features.</li>
              <li>You can request deletion of your account and associated data by contacting support.</li>
              <li>We may use anonymized, aggregated data (that cannot identify you) to improve the Service.</li>
            </ul>
          </section>

          <Separator />

          {/* Intellectual Property */}
          <section>
            <h2 className="text-xl font-semibold">9. Intellectual Property</h2>
            <p>
              The Service, including its design, features, code, graphics, logos, and content (excluding your data), is owned by MC Capital Solutions LLC and is protected by copyright, trademark, and other intellectual property laws.
            </p>
            <ul className="list-disc pl-6 space-y-1">
              <li>You may not copy, modify, distribute, or create derivative works of any part of the Service.</li>
              <li>"HostCaptain" and the HostCaptain logo are trademarks of MC Capital Solutions LLC.</li>
            </ul>
          </section>

          <Separator />

          {/* Account Termination */}
          <section>
            <h2 className="text-xl font-semibold">10. Account Termination</h2>
            <p>We reserve the right to suspend or terminate your account if:</p>
            <ul className="list-disc pl-6 space-y-1">
              <li>You violate these Terms or our Acceptable Use policy.</li>
              <li>Your account is used for fraudulent, abusive, or illegal activity.</li>
              <li>Your payment method fails and remains unresolved after reasonable notice.</li>
            </ul>
            <p>
              You may delete your account at any time from your account settings. Upon deletion, your data will be removed in accordance with our{" "}
              <Link to="/privacy" className="text-primary hover:underline">Privacy Policy</Link>.
            </p>
          </section>

          <Separator />

          {/* Limitation of Liability */}
          <section>
            <h2 className="text-xl font-semibold">11. Limitation of Liability</h2>
            <p className="uppercase text-sm font-semibold text-muted-foreground">
              TO THE MAXIMUM EXTENT PERMITTED BY APPLICABLE LAW:
            </p>
            <ul className="list-disc pl-6 space-y-1 mt-2">
              <li>The Service is provided "AS IS" and "AS AVAILABLE" without warranties of any kind, express or implied.</li>
              <li>We do not warrant that the Service will be uninterrupted, error-free, or completely secure.</li>
              <li>We do not guarantee the accuracy of any calculations, analytics, reports, or AI-generated content provided by the Service.</li>
              <li>In no event shall MC Capital Solutions LLC, its officers, directors, employees, or agents be liable for any indirect, incidental, special, consequential, or punitive damages, including but not limited to loss of profits, data, or business opportunities.</li>
              <li>Our total aggregate liability for any claims arising from or related to the Service shall not exceed the total amount you have paid us in the twelve (12) months preceding the claim.</li>
            </ul>
          </section>

          <Separator />

          {/* Indemnification */}
          <section>
            <h2 className="text-xl font-semibold">12. Indemnification</h2>
            <p>
              You agree to indemnify, defend, and hold harmless MC Capital Solutions LLC from and against any claims, damages, losses, liabilities, costs, and expenses (including reasonable attorney's fees) arising from your use of the Service, your violation of these Terms, or your infringement of any third-party rights.
            </p>
          </section>

          <Separator />

          {/* Dispute Resolution */}
          <section>
            <h2 className="text-xl font-semibold">13. Dispute Resolution</h2>
            <ul className="list-disc pl-6 space-y-1">
              <li>These Terms shall be governed by and construed in accordance with the laws of the State of California, without regard to its conflict of law provisions.</li>
              <li>Any disputes arising from these Terms or the Service shall first be attempted to be resolved through good-faith negotiation.</li>
              <li>If negotiation fails, disputes shall be resolved through binding arbitration in accordance with the rules of the American Arbitration Association, conducted in California.</li>
              <li>You agree to waive any right to a jury trial or to participate in a class action lawsuit.</li>
            </ul>
          </section>

          <Separator />

          {/* Changes to Terms */}
          <section>
            <h2 className="text-xl font-semibold">14. Changes to These Terms</h2>
            <p>
              We may update these Terms from time to time. When we make material changes, we will notify you by email or by posting a prominent notice on the Service at least 30 days before the changes take effect. Your continued use of HostCaptain after the updated Terms take effect constitutes acceptance of the revised Terms.
            </p>
          </section>

          <Separator />

          {/* Severability */}
          <section>
            <h2 className="text-xl font-semibold">15. Severability</h2>
            <p>
              If any provision of these Terms is found to be unenforceable or invalid, that provision shall be limited or eliminated to the minimum extent necessary so that these Terms shall otherwise remain in full force and effect.
            </p>
          </section>

          <Separator />

          {/* Contact */}
          <section>
            <h2 className="text-xl font-semibold">16. Contact Us</h2>
            <p>If you have any questions about these Terms, please contact us at:</p>
            <div className="bg-card border rounded-lg p-4 mt-2">
              <p className="font-semibold mb-1">MC Capital Solutions LLC</p>
              <p className="text-muted-foreground mb-1">DBA HostCaptain</p>
              <p className="mb-0">
                Email:{" "}
                <a href="mailto:support@hostcaptain.co" className="text-primary hover:underline">
                  support@hostcaptain.co
                </a>
              </p>
            </div>
          </section>

        </div>
      </main>

      {/* Footer */}
      <footer className="bg-navy text-white py-12 mt-16">
        <div className="container mx-auto px-6">
          <div className="flex flex-col md:flex-row justify-between items-center">
            <div className="flex items-center gap-2 mb-4 md:mb-0">
              <AnchorIcon className="h-6 w-6" />
              <span className="font-bold">HostCaptain</span>
            </div>
            <div className="flex gap-8 text-sm text-white/60">
              <Link to="/pricing" className="hover:text-white transition-colors">Pricing</Link>
              <Link to="/privacy" className="hover:text-white transition-colors">Privacy Policy</Link>
              <Link to="/terms" className="hover:text-white transition-colors">Terms of Service</Link>
              <Link to="/auth" className="hover:text-white transition-colors">Login</Link>
            </div>
          </div>
          <div className="mt-6 text-center text-xs text-white/40">
            By using HostCaptain, you agree to our{" "}
            <Link to="/privacy" className="hover:text-white/60 underline">Privacy Policy</Link> and{" "}
            <Link to="/terms" className="hover:text-white/60 underline">Terms of Service</Link>.
          </div>
          <div className="mt-4 pt-4 border-t border-white/10 text-center text-sm text-white/40">
            © {new Date().getFullYear()} HostCaptain. All rights reserved.
          </div>
        </div>
      </footer>
    </div>
  );
};

export default TermsOfService;
